import React, { useMemo } from 'react';
import { Table, Button, Space, Tag, Tooltip } from 'antd';
import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { HQDisplayGroup, HQDisplayGroupTableProps, TableColumnKey, DisplayGroupStatus } from '../../../types/hqDisplayGroup';
// 修改引用路径，添加国际化状态标签函数
import { DISPLAY_GROUP_STATUS_COLORS, getStatusLabel } from '../../../utils/hqDisplayGroupConstants';
import dayjs from 'dayjs';

const HQDisplayGroupTable: React.FC<HQDisplayGroupTableProps> = ({
  data,
  pagination,
  loading,
  onPageChange,
  onSortChange,
  onRefresh,
  onView,
  onEdit,
  selectedRowKeys,
  setSelectedRowKeys,
}) => {
  const { t } = useTranslation();

  // Handle row selection change
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys as string[]);
  };

  // Row selection config
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: HQDisplayGroup) => ({
      disabled: false, // Disable selection if has associated store groups
    }),
  };

  // Table columns
  const columns = [
    {
      title: t('hqDisplayGroup.table.code'),
      dataIndex: 'code',
      key: 'code' as TableColumnKey,
      // sorter: true,
      width: 120,
      fixed: 'left' as const,
      render: (text: string) => <span className="code-column">{text}</span>,
    },
    {
      title: t('hqDisplayGroup.table.name'),
      dataIndex: 'name',
      key: 'name' as TableColumnKey,
      // sorter: true,
      width: 180,
    },
    {
      title: t('hqDisplayGroup.table.status'),
      dataIndex: 'status',
      key: 'status' as TableColumnKey,
      // sorter: true,
      width: 100,
      render: (status: DisplayGroupStatus) => (
        <Tag color={DISPLAY_GROUP_STATUS_COLORS[status]} className="status-tag">
          {getStatusLabel(status, t)}
        </Tag>
      ),
    },
    {
      title: t('hqDisplayGroup.table.mainDivision'),
      dataIndex: 'mainDivisionName',
      key: 'mainDivision' as TableColumnKey,
      // sorter: true,
      width: 150,
    },
    {
      title: t('hqDisplayGroup.table.categoryCount'),
      dataIndex: 'subClassCount',
      key: 'subClassCount' as TableColumnKey,
      width: 120
    },
    {
      title: t('hqDisplayGroup.table.hasWhitelistItems'),
      dataIndex: 'hasWhitelistItems',
      key: 'hasWhitelistItems' as TableColumnKey,
      // sorter: true,
      width: 120,
      render: (value: 'YES' | 'NO') => (
        <Tag color={value === 'YES' ? 'green' : 'default'}>
          {value === 'YES' ? t('common.yes') : t('common.no')}
        </Tag>
      ),
    },
    {
      title: t('hqDisplayGroup.table.hasBlacklistItems'),
      dataIndex: 'hasBlacklistItems',
      key: 'hasBlacklistItems' as TableColumnKey,
      // sorter: true,
      width: 120,
      render: (value: 'YES' | 'NO') => (
        <Tag color={value === 'YES' ? 'red' : 'default'}>
          {value === 'YES' ? t('common.yes') : t('common.no')}
        </Tag>
      ),
    },
    {
      title: t('hqDisplayGroup.table.usedByStoreGroup'),
      dataIndex: 'usedByStoreGroup',
      key: 'usedByStoreGroup' as TableColumnKey,
      // sorter: true,
      width: 120,
      render: (value) => (
        <Tag color={value ? 'red' : 'default'}>
          {value ? t('common.yes') : t('common.no')}
        </Tag>
      ),
    },
    {
      title: t('hqDisplayGroup.table.saleDays'),
      dataIndex: 'saleDays',
      key: 'saleDays' as TableColumnKey,
      width: 120,
    },
    // {
    //   title: '关联门店组数',
    //   dataIndex: 'associatedStoreGroups',
    //   key: 'associatedStoreGroups' as TableColumnKey,
    //   sorter: true,
    //   width: 120,
    // },
    {
      title: t('hqDisplayGroup.table.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate' as TableColumnKey,
      // sorter: true,
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t('common.creator'),
      dataIndex: 'creatorName',
      key: 'creatorName' as TableColumnKey,
      // sorter: true,
      width: 120,
    },
    {
      title: t('hqDisplayGroup.table.updateTime'),
      dataIndex: 'gmtModified',
      key: 'gmtModified' as TableColumnKey,
      // sorter: true,
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t('common.modifier'),
      dataIndex: 'modifierName',
      key: 'modifierName' as TableColumnKey,
      // sorter: true,
      width: 120,
    },
    {
      title: t('hqDisplayGroup.table.actions'),
      key: 'action',
      fixed: 'right' as const,
      width: 120,
      render: (_: any, record: HQDisplayGroup) => (
        <Space size="middle">
          <Tooltip title={t('hqDisplayGroup.viewDetails')}>
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => onView(record.code)}
            />
          </Tooltip>
          <Tooltip title={t('hqDisplayGroup.editItem')}>
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => onEdit(record.code)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Handle table change (sorting, pagination)
  const handleTableChange = (pagination: any, _: any, sorter: any) => {
    const { current, pageSize } = pagination;
    const { field, order } = sorter;

    onPageChange(current, pageSize);
    if (field && order) {
      // 通知父组件排序字段和顺序，但不触发后端调用
      // 父组件应该在本地处理排序逻辑
      onSortChange(field as TableColumnKey, order === 'ascend' ? 'asc' : 'desc');
    } else {
      // Reset to default sorting
      onSortChange('createTime', 'desc');
    }
  };

   const formatDateTime = (timestamp: number): string => {
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };

  return (
    <Table
      rowSelection={rowSelection}
      columns={columns}
      dataSource={data} // 直接使用父组件传递的已排序数据
      rowKey="id"
      loading={loading}
      pagination={{
        current: pagination.page,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => t('hqDisplayGroup.totalRecords', { total }),
        pageSizeOptions: ['10', '20', '50', '100'],
      }}
      onChange={handleTableChange}
      scroll={{ x: 1500, y: 500 }}
      size="middle"
    />
  );
};

export default HQDisplayGroupTable;